/**
 * Gravatar lookup for contacts that have an email address.
 *
 * Only three of our channels can produce a face at all: Telegram exposes one
 * through the Bot API (fetched server-side into our own S3 — see
 * channels/telegram/avatar.js), and email has no avatar in the protocol, so
 * Gravatar is the one source for the channel most contacts arrive on. WhatsApp
 * Cloud and SMS expose nothing, and those contacts stay on initials.
 *
 * Privacy: the request sends a SHA-256 of the contact's address to
 * gravatar.com, from the agent's browser. `d=404` means Gravatar answers with a
 * 404 rather than a generated image when it has never seen that address, which
 * is what lets the <img> fall through to initials instead of showing a stranger
 * a robot. Nothing else about the contact leaves the console.
 */
const CACHE = new Map<string, string>();

const hex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

/** Gravatar's own normalisation: trim, lowercase, hash. */
export const normalizeEmail = (email: string) => email.trim().toLowerCase();

/**
 * Resolves to the avatar url, or null when the address is unusable or the
 * browser has no SubtleCrypto (non-secure origin — an http:// preview host).
 */
export async function gravatarUrl(email: string | null, size = 96): Promise<string | null> {
  const address = normalizeEmail(email ?? '');
  if (!address.includes('@')) return null;
  const key = `${address}:${size}`;
  const hit = CACHE.get(key);
  if (hit) return hit;
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await globalThis.crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(address),
    );
    const url = `https://gravatar.com/avatar/${hex(digest)}?s=${size}&d=404`;
    CACHE.set(key, url);
    return url;
  } catch {
    return null;
  }
}
